<template>
  <div>
    <v-overlay :value="showLoader">
      <v-progress-circular
        indeterminate
        color="primary"
        :size="70"
        :width="7"
      ></v-progress-circular>
    </v-overlay>
    <UniqueEmailDialog
      loggedIn
      :showDialog="showNonUniqueEmail"
      :email="contactInfo.email"
      :onCheck="checkEmail"
      :onClose="() => showNonUniqueEmail = false"
    />
    <VerifyCodeDialog
      :verificationType="emailVerificationType"
      :contactInfo="contactInfo.email"
      :showDialog="showEmailCodeDialog"
      :onClose="closeEmailCodeDialog"
      :onSuccess="emailVerifiedSuccessfully"
      buttonText="Verify & Update"
    />
    <VerifyCodeDialog
      :verificationType="phoneNumberVerificationType"
      :contactInfo="contactInfo.phoneNumber"
      :showDialog="showSmsCodeDialog"
      :onClose="closeSmsCodeDialog"
      :onSuccess="phoneNumberVerifiedSuccessfully"
      buttonText="Verify & Update"
    />
    <v-alert class="mb-2 mt-1" v-if="errorMessage" :type="'error'">
      {{ errorMessage }}
    </v-alert>
    <v-alert class="mb-2 mt-1"  type="warning" outlined
      v-if="!((!!contactInfo.phoneNumber || !!contactInfo.email) 
              && ((!!contactInfo.phoneNumber && contactInfo.phoneNumberVerified) || (!!contactInfo.email && contactInfo.emailVerified)))">
      Please, provide and verify your email address and/or mobile phone number to ensure you receive important notifications.      
    </v-alert>
    <v-card class="user-profile-card" v-if="contactInfo">
      <h3 class="mb-3">Contact Info</h3>
      <p>
        Note: If you are verifying your email address or phone number, you need to
        click "Update contact" button to save the verification result.
      </p>
      <v-form ref="contactForm" v-model="isValid" dense>
        <v-row class="ma-0">
          <v-col>
            <v-text-field
              outlined
              label="First Name"
              v-model="contactInfo.firstName"
              :rules="[(v) => !!v || 'First name is required']"
            />
          </v-col>
          <v-col>
            <v-text-field
              outlined
              label="Last Name"
              v-model="contactInfo.lastName"
              :rules="[(v) => !!v || 'Last name is required']"
            />
          </v-col>
        </v-row>
        <v-row class="ma-0">
          <v-col>
            <v-row class="ma-0">
              <v-col cols="9" class="pl-0">
                <v-text-field
                  outlined
                  label="Email Address"
                  v-model="contactInfo.email"
                  :rules="[validateEmail]"
                />
              </v-col>
              <v-col cols="3">
                <span color="green" v-if="contactInfo.emailVerified">
                  <font-awesome-icon color="green" icon="check" /> Verified
                </span>
                <span v-if="contactInfo.email && !contactInfo.emailVerified">
                  <v-btn
                    large
                    color="primary"
                    v-on:click="verifyEmail"
                    >Verify</v-btn
                  >
                </span>
              </v-col>
            </v-row>
          </v-col>
        </v-row>
        <v-row class="ma-0">
          <v-col>
            <v-row class="ma-0">
              <v-col cols="9" class="pl-0">
                <InternationalPhoneNumberInput
                  :phone="contactInfo.phoneNumber"
                  @changeData="(v) => contactInfo.phoneNumber = v.number.e164"
                  @validate="v => isPhoneValid = v"
                />
              </v-col>
              <v-col cols="3">
                <span v-if="contactInfo.phoneNumberVerified">
                  <font-awesome-icon color="green" icon="check" /> Verified
                </span>
                <span
                  v-if="
                    contactInfo.phoneNumber && !contactInfo.phoneNumberVerified
                  "
                >
                  <v-btn
                    large
                    color="primary"
                    v-on:click="showSmsCodeDialog = true"
                    >Verify</v-btn
                  >
                </span>
              </v-col>
            </v-row>
          </v-col>
        </v-row>
        <v-row class="ma-0">
          <v-col>
            <v-text-field
              outlined
              label="Address"
              v-model="contactInfo.address.street"
              :rules="[(v) => !!v || 'Address is required']"
            />
          </v-col>
          <v-col>
            <v-text-field
              outlined
              label="City"
              v-model="contactInfo.address.city"
              :rules="[validateCity]"
            />
          </v-col>
        </v-row>
        <v-row class="ma-0">
          <v-col>
            <country-dropdown
              :selectedCountry="contactInfo.address.country"
              v-on:update="updateCountry"
            />
          </v-col>
          <v-col>
            <state-dropdown
              v-if="
                !contactInfo.address.country ||
                contactInfo.address.country === country_USA
              "
              required
              :selectedState="contactInfo.address.state"
              v-on:update="contactInfo.address.state = $event"
            />
            <v-text-field
              v-if="
                contactInfo.address.country &&
                contactInfo.address.country !== country_USA
              "
              outlined
              v-model="contactInfo.address.international"
              label="International Area"
              :rules="[(v) => !!v || 'International Area is required']"
            ></v-text-field>
          </v-col>
          <v-col>
            <v-text-field
              outlined
              label="Zip/Postal"
              v-model="contactInfo.address.zip"
              :rules="[validateZip]"
            />
          </v-col>
        </v-row>
        <v-row class="ma-0">
          <v-spacer />
          <v-btn
            large
            color="primary"
            :disabled="!canSubmit()"
            @click="updateContactInfo"
            >Update contact</v-btn
          >
        </v-row>
      </v-form>
    </v-card>
  </div>
</template>

<script lang="ts">
import Vue from "vue";

import VerifyCodeDialog from '@/components/common/VerifyCodeDialog.vue';
import UniqueEmailDialog from '@/components/common/UniqueEmailDialog.vue';
import CountryDropdown from '@/components/common/inputs/CountryDropdown.vue';
import StateDropdown from '@/components/common/inputs/StateDropdown.vue';
import InternationalPhoneNumberInput from '@/components/common/inputs/InternationalPhoneNumberInput.vue';

import personService from "@/services/portal/personService";
import authenticationService from "@/services/authenticationService";
import { VerificationType } from "@/services/codeVerificationService";

import { UserInfo } from "@/types/User";
import { ContactInfo } from "@/types/portal/Person";

import validateEmail from '@/helpers/validate-email';
import normalizeAddressRegion from '@/helpers/normalize-address-region';
import { Countries } from "@/constants";
import { getErrorMessage } from '@/services/errorHandler';

export default Vue.extend({
  name: "ContactInfo",
  components: {
    VerifyCodeDialog,
    CountryDropdown,
    StateDropdown,
    UniqueEmailDialog,
    InternationalPhoneNumberInput,
  },
  data: function () {
    return {
      errorMessage: "",
      errorMessageInsideModal: "",
      showNonUniqueEmail: false,
      isValid: false,
      isValidPhone: false,
      contactInfo: <ContactInfo>{
        firstName: "",
        lastName: "",
        address: {
          street: "",
        }
      },
      persistedContactInfo: <ContactInfo>{},
      showLoader: false,
      showEmailCodeDialog: false,
      showSmsCodeDialog: false,
      country_USA: Countries.USA,    
    };
  },
  created: function () {
    this.loadUserContactInfo();
  },
  watch: {
    userInfo: function (newVal, oldVal) {
      if (newVal === oldVal) return;

      this.loadUserContactInfo();
    },
    "contactInfo.phoneNumber": function (newVal, oldVal) {
      if (newVal != this.persistedContactInfo.phoneNumber) {
        this.contactInfo.phoneNumberVerified = false;
      } else {
        this.contactInfo.phoneNumberVerified =
          this.persistedContactInfo.phoneNumberVerified;
      }
    },
    "contactInfo.email": function (newVal, oldVal) {
      if (newVal != this.persistedContactInfo.email) {
        this.contactInfo.emailVerified = false;
      } else {
        this.contactInfo.emailVerified =
          this.persistedContactInfo.emailVerified;
      }
    },
  },
  computed: {
    userInfo(): UserInfo {
      return this.$store.state.userInfo;
    },
    emailVerificationType: function () {
      return VerificationType.Email;
    },
    phoneNumberVerificationType: function () {
      return VerificationType.PhoneNumber;
    },
  },
  methods: {
    validateEmail(v: string) {
      return (
        validateEmail(v) ||
        "E-mail must be valid"
      );
    },
    validateCity(v: string) {
      return this.contactInfo.address.country === Countries.USA
        ? !!v || 'City is required'
        : true
    },
    validateZip(v: string) {
      return this.contactInfo.address.country === Countries.USA
        ? !!v || 'Zip/Postal is required'
        : true
    },
    updateCountry: async function (v: string) {
      this.contactInfo.address.country = v;
      this.clearInactiveAddressField();

      await Vue.nextTick();

      (this.$refs.contactForm as any).validate();
    },
    clearInactiveAddressField: function () {
      const address = this.contactInfo.address;
      const region = normalizeAddressRegion(
        address.country,
        address.state,
        address.international
      );
      address.state = region.state;
      address.international = region.international;
    },
    updateContactInfo: function () {
      this.clearInactiveAddressField();
      this.showLoader = true;
      personService
        .changeAddressInformation(
          this.$data.contactInfo
        )
        .then((res) => {
          if (res.errorMessage) {
            this.errorMessage = res.errorMessage;
            this.showLoader = false;
          } else {
            this.errorMessage = "";
            this.loadUserContactInfo();
          }
        });
    },
    closeEmailCodeDialog: function () {
      this.showEmailCodeDialog = false;
    },
    emailVerifiedSuccessfully: function () {
      this.contactInfo.emailVerified = true;
      this.showEmailCodeDialog = false;

      this.showLoader = true;
      personService
        .changeContactInformation(
          this.$data.contactInfo
        )
        .then((res) => {
          if (res.errorMessage) {
            this.errorMessage = res.errorMessage;
            this.showLoader = false;
          } else {
            this.errorMessage = "";
            this.loadUserContactInfo();
          }
        });
    },
    closeSmsCodeDialog: function () {
      this.showSmsCodeDialog = false;
    },
    phoneNumberVerifiedSuccessfully: function () {
      this.contactInfo.phoneNumberVerified = true;
      this.showSmsCodeDialog = false;

      this.showLoader = true;
      personService
        .changeContactInformation(
          this.$data.contactInfo
        )
        .then((res) => {
          if (res.errorMessage) {
            this.errorMessage = res.errorMessage;
            this.showLoader = false;
          } else {
            this.errorMessage = "";
            this.loadUserContactInfo();
          }
        });
    },
    checkEmail: async function(newEmail?: string) {
      if (this.contactInfo.email === this.userInfo.email) return true;

      if (!!newEmail) this.contactInfo.email = newEmail;

      try {
        const response = await authenticationService.uniqueEmail(this.contactInfo.email);

        this.showNonUniqueEmail = !response.unique;

        return response.unique;
      } catch (error) {
        this.errorMessage = getErrorMessage(error);
        return false;
      }
    },
    verifyEmail: async function() {
      const uniqueEmail = await this.checkEmail();

      if (uniqueEmail) this.showEmailCodeDialog = true
    },
    canSubmit: function () {
      return (
        JSON.stringify(this.contactInfo) !==
          JSON.stringify(this.persistedContactInfo)
        && this.isValid
      );
    },
    loadUserContactInfo: function () {
      this.showLoader = true;
      personService
        .getContactInformation()
        .then((res) => {
          if (res.found) {
            this.contactInfo = Object.assign({}, res.data);
            this.clearInactiveAddressField();
            this.persistedContactInfo = Object.assign({}, res.data);
            this.persistedContactInfo.address = Object.assign(
              {},
              res.data.address
            );
          } else {
            this.errorMessage = "Couldn't load the person contact information.";
          }
          this.showLoader = false;
        })
        .catch(this.$handleError);
    },
  },
});
</script>
